import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { CloudUpload, Search, X } from 'lucide-react';
import noBrandImage from '../../assets/img/brand/no-brand-img.png';
import emptySearchImg from '../../assets/img/brand/empty-search.png';
import Pagination from '../../components/Pagination';
import TradeInImportImagesModal from './TradeInImportImagesModal.js';
import { listLibraryRange, systemLibraryImages } from './tradeInMockApi.js';
import {
    YELLOW,
    noImage,
    isDeviceDefaultUrl,
    filenameFromUrl,
    sameImageUrl,
    tabForSource,
    SOURCE_ITEM,
} from './tradeInImages.js';

// ─── Trade-In Image Picker (ERP-5289) ─────────────────────────────────────────
// "Select or Upload Listing Item Image", opened by Reselect in Step 2.
//
//   Upload  the shared Listing Image library — searchable by name, 16 per page (paged on the
//           server), with Import to
//           add local files to the library without leaving the picker. This listing's own
//           images (saved uploads + anything picked earlier in this session) are shown with it,
//           and the current device's placeholder comes first.
//   SPU     the referenced SPU's images.
//
// ERP-5298 (PRD 2.3): the item's current image is selected when the picker opens — the device
// placeholder for a new item — and the grid opens on its page. Import adds to the library
// without the rename step the Listing Image tab offers.
//
// The pick is handed back in the shapes today's save payload understands (see Step 3's
// buildImageFields): the current device placeholder keeps its "device default" meaning, a saved
// upload is referenced by id + source, and a library image by its id with tab 'library'
// (ERP-5297) — the backend links the item to that library row. Nothing is deleted from here —
// images are managed on the Listing Image tab.

const PAGE_SIZE = 16;
const FONT = 'Montserrat, sans-serif';
const DEVICE_KEY = 'device';
const INITIAL_KEY = '__initial__';
const isPoolTab = (tab) => tab === 'tradein' || tab === 'listing';

async function fileFromUrl(url, name) {
    const res = await fetch(url);
    const blob = await res.blob();
    return new File([blob], name, { type: blob.type || 'image/png' });
}

function Tile({ tile, selected, onPick, showName }) {
    return (
        <div onClick={onPick} title={tile.name} style={{ width: 115, display: 'flex', flexDirection: 'column', gap: 5, cursor: 'pointer' }}>
            <div style={{
                width: 115, height: 115, boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 10, borderRadius: 8, background: '#FFFFFF',
                boxShadow: selected ? `0 0 0 2px ${YELLOW}` : 'inset 0 0 0 1px #DEE2E6',
            }}>
                <img
                    src={tile.src || noImage}
                    alt={tile.name}
                    onError={e => { e.target.onerror = null; e.target.src = noImage; }}
                    style={{ width: 95, height: 95, objectFit: 'contain', mixBlendMode: 'darken' }}
                />
            </div>
            {showName && (
                <span style={{
                    fontSize: 8, lineHeight: 1.3, color: selected ? '#212529' : '#6C757D', wordBreak: 'break-all',
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                }}>
                    {tile.name}
                </span>
            )}
        </div>
    );
}

function EmptyState({ image, title, message }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '20px 0 30px' }}>
            <img src={image} alt="" style={{ width: 90, height: 90, objectFit: 'contain' }} />
            <span style={{ fontSize: 20, fontWeight: 600, color: '#212529', textAlign: 'center' }}>{title}</span>
            <span style={{ fontSize: 14, color: '#6C757D', textAlign: 'center' }}>{message}</span>
        </div>
    );
}

export default function TradeInImagePickerModal({
    imageLabel = 'Listing Item Image',
    ownSource = SOURCE_ITEM,
    images = [],
    initialSelected,
    initialUploadedImages,
    sessionUploads,
    onAddSessionUpload,
    uploadCounterRef,
    deviceDefaultImage,
    excludeListingId,
    onCancel,
    onConfirm,
}) {
    const [importOpen, setImportOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [search, setSearch] = useState('');      // `query`, debounced — what the server searches
    const [page, setPage] = useState(1);
    const [confirming, setConfirming] = useState(false);
    // The library is paged on the server: only the rows the current page shows are loaded.
    const [serverRows, setServerRows] = useState(null);   // null while the first page loads
    const [serverCount, setServerCount] = useState(0);
    const [reloadKey, setReloadKey] = useState(0);
    // The picked tile itself, so the pick survives turning to a page that doesn't show it.
    const [pickedTile, setPickedTile] = useState(null);

    useEffect(() => {
        const t = setTimeout(() => setSearch(query.trim()), 300);
        return () => clearTimeout(t);
    }, [query]);

    // This listing's own images: the level's saved history plus the listing-wide session pool.
    // One tile per filename — every row keeps its own copy of a file it uses, so the same photo
    // arrives under several ids.
    const pool = useMemo(() => {
        const seen = new Set();
        const out = [];
        [...(initialUploadedImages || []), ...(sessionUploads || [])].forEach(u => {
            if (!u || u.error || !u.preview || isDeviceDefaultUrl(u.preview)) return;
            const name = u.name || filenameFromUrl(u.preview) || '';
            const key = name ? name.toLowerCase() : u.id;
            if (seen.has(key)) return;
            seen.add(key);
            out.push({ ...u, name, source: u.source || ownSource });
        });
        return out;
    }, [initialUploadedImages, sessionUploads, ownSource]);

    // Tiles held in the browser, shown ahead of the library: the current device's placeholder,
    // this listing's own images, then the other devices' placeholders.
    const localTiles = useMemo(() => {
        const system = systemLibraryImages();
        const current = system.find(l => l.url === deviceDefaultImage);
        const tiles = [];
        if (current) tiles.push({ key: DEVICE_KEY, kind: 'device', name: current.name, src: current.url, entry: current });
        pool.forEach(p => tiles.push({ key: `pool:${p.source}:${p.id}`, kind: 'pool', name: p.name, src: p.preview, entry: p }));
        system.forEach(l => {
            if (l !== current) tiles.push({ key: `lib:${l.id}`, kind: 'lib', name: l.name, src: l.url, entry: l });
        });
        return tiles;
    }, [pool, deviceDefaultImage]);

    const q = search.toLowerCase();
    const localFiltered = useMemo(
        () => localTiles.filter(t => t.name.toLowerCase().includes(q)),
        [localTiles, q],
    );

    // Where the page starts, in local tiles first and then library rows.
    const pageStart = (page - 1) * PAGE_SIZE;
    const localSlice = localFiltered.slice(pageStart, pageStart + PAGE_SIZE);
    const serverOffset = Math.max(0, pageStart - localFiltered.length);
    const serverLimit = PAGE_SIZE - localSlice.length;

    useEffect(() => {
        let stale = false;
        listLibraryRange({ search, offset: serverOffset, limit: serverLimit })
            .then(({ results, count }) => {
                if (stale) return;
                setServerRows(results);
                setServerCount(count);
            })
            .catch(err => {
                console.error('Failed to load trade-in images:', err);
                if (!stale) setServerRows(prev => prev || []);
            });
        return () => { stale = true; };
    }, [search, serverOffset, serverLimit, reloadKey]);

    const libTiles = (serverRows || []).map(l => ({ key: `lib:${l.id}`, kind: 'lib', name: l.name, src: l.url, entry: l }));
    const pageTiles = [...localSlice, ...libTiles];
    const total = localFiltered.length + serverCount;
    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const safePage = Math.min(page, pageCount);

    const spuTiles = useMemo(() => images.map((img, i) => ({
        key: `spu:${img.id ?? img.url}`,
        kind: 'spu',
        name: filenameFromUrl(img.url) || `SPU image ${i + 1}`,
        src: img.url,
        entry: img,
    })), [images]);

    // Which tile the current image is. Anything that can't be matched (e.g. an SPU snapshot
    // saved before) stays selected as-is and is shown in the footer only.
    const [initialKey] = useState(() => {
        const init = initialSelected;
        if (!init) return null;
        if (init.isDeviceDefault || isDeviceDefaultUrl(init.url)) return DEVICE_KEY;
        if (init.tab === 'library' && init.id != null) return `lib:${init.id}`;
        if (!init.isUploaded && !isPoolTab(init.tab)) {
            const spu = images.find(s => (init.id != null && s.id === init.id) || sameImageUrl(s.url, init.url));
            if (spu) return `spu:${spu.id ?? spu.url}`;
        }
        const p = pool.find(u => init.id != null && u.id === init.id && (!init.source || u.source === init.source))
            || (init.name && pool.find(u => u.name === init.name))
            || pool.find(u => sameImageUrl(u.preview, init.url));
        return p ? `pool:${p.source}:${p.id}` : INITIAL_KEY;
    });
    const [selectedKey, setSelectedKey] = useState(initialKey);
    const [activeTab, setActiveTab] = useState(() => (
        (initialKey && initialKey.startsWith('spu:')) || (initialKey === INITIAL_KEY && initialSelected?.tab === 'spu')
            ? 'spu' : 'upload'
    ));

    const pick = (tile) => {
        setSelectedKey(tile.key);
        setPickedTile(tile);
    };

    // Turn to the page holding the library image `locate` names and select it.
    const showLibraryImage = async (locate) => {
        const { located } = await listLibraryRange({ limit: 0, ...locate });
        if (!located) return false;
        setSelectedKey(`lib:${located.id}`);
        setPickedTile(null);
        setPage(Math.floor((localTiles.length + located.index) / PAGE_SIZE) + 1);
        return true;
    };

    // Open on the page holding the current image (edit flow). A local tile's page is known
    // here; a saved item using a library image comes back as a plain snapshot (no pool row),
    // so the server finds it — by Image Name, or by stored file name (all trade-in files share
    // one flat folder, so the file name identifies the picture).
    useEffect(() => {
        if (!initialSelected) return;
        if (initialKey !== INITIAL_KEY && !initialKey.startsWith('lib:')) {
            const idx = localTiles.findIndex(t => t.key === initialKey);
            if (idx !== -1) setPage(Math.floor(idx / PAGE_SIZE) + 1);
            return;
        }
        showLibraryImage({
            locateName: initialSelected.name || '',
            locateFile: filenameFromUrl(initialSelected.url) || '',
        }).catch(err => console.error('Failed to find the current image:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const initialTile = { name: initialSelected?.name || filenameFromUrl(initialSelected?.url) || 'Current image', src: initialSelected?.url };
    // The current image keeps its footer while the library loads, or when its tile isn't listed.
    const selectedTile = selectedKey === INITIAL_KEY
        ? initialTile
        : [...pageTiles, ...spuTiles].find(t => t.key === selectedKey)
            || (pickedTile?.key === selectedKey ? pickedTile : null)
            || (selectedKey === initialKey ? initialTile : null);

    // The pick, in the shape Step 2/3 save, plus the upload row it needs (if any).
    const toSelection = async (tile) => {
        if (tile.kind === 'device') {
            return { selection: { id: null, url: tile.src, name: tile.name, isPlaceholder: true, isDeviceDefault: true }, upload: null };
        }
        if (tile.kind === 'spu') return { selection: { ...tile.entry, tab: 'spu' }, upload: null };
        if (tile.kind === 'pool') {
            const p = tile.entry;
            const selection = p.isSaved
                ? { id: p.id, url: p.preview, name: p.name, source: p.source, tab: tabForSource(p.source) }
                : { id: p.id, url: p.preview, file: p.file, name: p.name, isUploaded: true, source: p.source };
            return { selection, upload: p };
        }
        const l = tile.entry;
        // A stored library image is referenced, not re-uploaded (ERP-5297).
        if (!l.isSystem) {
            return { selection: { id: l.id, url: l.url, name: l.name, tab: 'library' }, upload: null };
        }
        // Another device's placeholder is saved as a fresh upload of its file, with its own
        // object URL so the steps don't mistake it for the device default.
        const file = l.file || await fileFromUrl(l.url, l.name);
        const preview = l.isSystem ? URL.createObjectURL(file) : l.url;
        uploadCounterRef.current += 1;
        const id = `upload-${uploadCounterRef.current}`;
        const upload = { id, file, preview, name: l.name, source: ownSource };
        if (onAddSessionUpload) onAddSessionUpload(upload);
        return { selection: { id, url: preview, file, name: l.name, isUploaded: true, source: ownSource }, upload };
    };

    const handleConfirm = async () => {
        if (!selectedKey || confirming) return;
        if (selectedKey === initialKey) {
            onConfirm(initialSelected, initialUploadedImages || [], []);
            return;
        }
        if (!selectedTile) return;
        setConfirming(true);
        try {
            const { selection, upload } = await toSelection(selectedTile);
            const saved = (initialUploadedImages || []).filter(u => u.isSaved);
            const uploads = upload && !saved.some(u => u.id === upload.id) ? [...saved, upload] : saved;
            onConfirm(selection, uploads, []);
        } catch (err) {
            console.error('Failed to use the selected image:', err);
            setConfirming(false);
        }
    };

    const tabButton = (key, label) => (
        <button
            key={key}
            type="button"
            onClick={() => setActiveTab(key)}
            style={{
                minWidth: 88, padding: 10, border: 'none', borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                background: activeTab === key ? YELLOW : 'transparent', color: activeTab === key ? '#212529' : '#6C757D',
            }}
        >
            {label}
        </button>
    );

    const btn = { height: 34, minWidth: 100, padding: '0 10px', borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, boxShadow: '0 1px 4px rgba(0,0,0,0.1)', cursor: 'pointer' };
    const canConfirm = Boolean(selectedTile) && !confirming;

    return createPortal(
        <>
            <div
                onClick={onCancel}
                style={{ position: 'fixed', inset: 0, zIndex: 1040, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(33,37,41,0.45)' }}
            >
                <div
                    onClick={e => e.stopPropagation()}
                    role="dialog"
                    aria-label={`Select or Upload ${imageLabel}`}
                    style={{
                        width: 'min(1100px, calc(100vw - 40px))', maxHeight: 'calc(100vh - 40px)', display: 'flex', flexDirection: 'column',
                        background: '#FFFFFF', borderRadius: 12, boxShadow: '0 4px 10px rgba(0,0,0,0.12)', fontFamily: FONT, color: '#212529', overflow: 'hidden',
                    }}
                >
                    <div style={{ height: 50, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px', boxSizing: 'border-box', background: '#F8F9FA' }}>
                        <span style={{ fontSize: 14, fontWeight: 600 }}>
                            Select or Upload {imageLabel}<span style={{ color: '#DC3545', fontSize: 12, fontWeight: 700, paddingLeft: 2 }}>*</span>
                        </span>
                        <button type="button" onClick={onCancel} title="Close" style={{ display: 'flex', padding: 0, border: 'none', background: 'none', cursor: 'pointer', color: '#212529' }}>
                            <X size={18} />
                        </button>
                    </div>

                    <div style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 10, padding: 20 }}>
                        <div style={{ display: 'flex', gap: 10, padding: 10, background: '#FFFFFF', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
                            {tabButton('upload', 'Upload')}
                            {tabButton('spu', 'SPU')}
                        </div>

                        {activeTab === 'upload' ? (
                            <>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <div style={{ flex: '0 1 260px', minWidth: 160, height: 40, display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px', boxSizing: 'border-box', border: '1px solid #6C757D', borderRadius: 8, background: '#FFFFFF' }}>
                                        <Search size={20} color="#6C757D" style={{ flexShrink: 0 }} />
                                        <input
                                            value={query}
                                            onChange={e => { setQuery(e.target.value); setPage(1); }}
                                            placeholder="Search Image Name..."
                                            style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontFamily: FONT, fontSize: 14, color: '#212529' }}
                                        />
                                        {query && (
                                            <button type="button" title="Clear" onClick={() => { setQuery(''); setPage(1); }} style={{ display: 'flex', padding: 0, border: 'none', background: 'none', cursor: 'pointer', color: '#6C757D' }}>
                                                <X size={16} />
                                            </button>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setImportOpen(true)}
                                        style={{ ...btn, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, border: 'none', background: '#212529', color: '#FFFFFF' }}
                                    >
                                        <CloudUpload size={18} />Import
                                    </button>
                                </div>

                                {serverRows === null ? (
                                    <span style={{ padding: '20px 0', fontSize: 12, color: '#6C757D' }}>Loading images…</span>
                                ) : total === 0 ? (
                                    <EmptyState image={emptySearchImg} title="No Image Found" message="Try a different image name." />
                                ) : (
                                    <>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 115px)', gap: 20, paddingTop: 10 }}>
                                            {pageTiles.map(t => (
                                                <Tile key={t.key} tile={t} selected={t.key === selectedKey} onPick={() => pick(t)} showName />
                                            ))}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, paddingTop: 10 }}>
                                            <span style={{ fontSize: 12, color: '#212529' }}>
                                                {(safePage - 1) * PAGE_SIZE + 1} - {(safePage - 1) * PAGE_SIZE + pageTiles.length} of {total}
                                            </span>
                                            <Pagination currentPage={safePage} totalPages={pageCount} onPageChange={setPage} />
                                        </div>
                                    </>
                                )}
                            </>
                        ) : spuTiles.length === 0 ? (
                            <EmptyState image={noBrandImage} title="No SPU Image Exists" message="Nothing here yet. Upload image for Trade-In." />
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 115px)', gap: 20, paddingTop: 10 }}>
                                {spuTiles.map(t => (
                                    <Tile key={t.key} tile={t} selected={t.key === selectedKey} onPick={() => pick(t)} />
                                ))}
                            </div>
                        )}
                    </div>

                    <div style={{ flexShrink: 0, padding: '0 20px 20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 9, paddingTop: 20, borderTop: '1px solid #CED4DA', minWidth: 0 }}>
                            {selectedTile ? (
                                <>
                                    <div style={{ width: 42, height: 42, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, background: '#FFFFFF', boxShadow: `0 0 0 2px ${YELLOW}` }}>
                                        <img
                                            src={selectedTile.src || noImage}
                                            alt=""
                                            onError={e => { e.target.onerror = null; e.target.src = noImage; }}
                                            style={{ width: 32, height: 32, objectFit: 'contain', mixBlendMode: 'darken' }}
                                        />
                                    </div>
                                    <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 5 }}>
                                        <span style={{ fontSize: 10, color: '#6C757D' }}>Selected</span>
                                        <span style={{ fontSize: 12, fontWeight: 700, color: '#000000', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedTile.name}</span>
                                    </div>
                                </>
                            ) : (
                                <span style={{ fontSize: 12, color: '#6C757D' }}>No image selected</span>
                            )}
                            <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
                                <button type="button" onClick={onCancel} style={{ ...btn, border: '1px solid #212529', background: '#FFFFFF', color: '#212529' }}>
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleConfirm}
                                    disabled={!canConfirm}
                                    style={{
                                        ...btn,
                                        ...(canConfirm
                                            ? { border: '1px solid #212529', background: '#212529', color: '#FFFFFF' }
                                            : { border: '1px solid #CED4DA', background: '#E9ECEF', color: '#6C757D', cursor: 'not-allowed' }),
                                    }}
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {importOpen && (
                <TradeInImportImagesModal
                    allowRename={false}
                    excludeListingId={excludeListingId}
                    onClose={() => setImportOpen(false)}
                    onImported={async (created) => {
                        // Back to the unfiltered library, on the page with the last imported image.
                        setQuery('');
                        setSearch('');
                        setReloadKey(k => k + 1);
                        const last = created[created.length - 1];
                        if (!last) return;
                        try {
                            await showLibraryImage({ locateName: last.name });
                        } catch (err) {
                            console.error('Failed to find the imported image:', err);
                        }
                    }}
                />
            )}
        </>,
        document.body,
    );
}
